import { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Backdrop,
  CircularProgress,
  Stack,
  Alert,
  TextField,
  Chip,
  Fade,
} from "@mui/material";
import { v4 as uuidv4 } from 'uuid';
import { red, yellow, green, blue } from '@mui/material/colors';
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Formik } from "formik";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as yup from "yup";
import { useSnackbar } from "notistack";
import { existRegulationWithNoCitation, getCitationLanguageWithoutPlaceHolder, getUTCDateTime, isFoundSystemChoicesBySubPartCode } from "../../utilities/helpers";
import CitationStatementChip from "./CitationStatementChip";
import { COVERAGE_CHOICE_CITATION_GUID, COVERAGE_CHOICE_COVERED_DISCUSS_ONLY_GUID, SNACKBAT_AUTO_HIDE_DURATION } from "../../utilities/constants";
import lookupApi from "../../apis/lookupApi";
import inspectionApi from "../../apis/inspectionApi";
// import { CloudUpload, Help, Note } from "@mui/icons-material";
import { CloudUpload} from "@mui/icons-material";
import Attachment from "./attachment";
import ModalDialog from "../../components/ModalDialog";
import NoteForm from "./note";
import SimpleAccordin from "../../components/SimpleAccordin";
import GptForm from "./gpt";
import assignmentApi from "../../apis/assignmentApi";
import EqipRichTextEditor from "../../components/EqipRichTextEditor";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { useRef } from "react";
import { create503bOperation, update503bOperation, delete503bOperation } from "../../apis/inspection503bOperationApi";
import { create503aOperation, update503aOperation, delete503aOperation } from "../../apis/inspection503aOperationApi";

// Short citation form
const Form = () => {
  const navigate = useNavigate();
  const loggedInUser = useSelector((state) => {
    return state.user;
  });
  const [errorMessage, setErrorMessage] = useState('');
  const debouncedSaveRef = useRef(null);
  const hasUserInteracted = useRef(false);
  const saving503BRef = useRef(false);
  const saving503ARef = useRef(false);
  const saveInProgressRef = useRef(false);
  const last503ASaveTimeRef = useRef(0);
  const SAVE_COOLDOWN_MS = 5000;

  const isNonMobile = useMediaQuery("(min-width:600px)");
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'idle', 'saving', 'saved', 'error'
  const [inspection, setInspection] = useState(null);
  const [selectedSystemChoice, setSelectedSystemChoice] = useState("1");
  const [selectedCoverageChoice, setSelectedCoverageChoice] = useState("1");
  const [selectedCitationLanguage, setSelectedCitationLanguage] = useState(null);
  const [selectedShortCitationLanguage, setSelectedShortCitationLanguage] = useState(null)
  const [initialValues, setInitialValues] = useState({
    systemChoice: null,
    coverageChoice: null,
    coverageComment: null,
    supporting: null,
    specifically: null,
    discussion: null,
    capa: null,
    description: null,
  });
  const { inspectionUid, subPartCode, citationLanguageCode, lookupcodetype, operationId } = useParams();
  
  // Check if this is a 503B operation form (new or existing)
  const isNew503BForm = window.location.pathname.includes('/503b/new');
  const isExisting503BForm = window.location.pathname.includes('/503b/') && operationId && operationId !== 'new';
  // Check if this is a 503A operation form (new or existing)
  const isNew503AForm = window.location.pathname.includes('/503a/new');
  const isExisting503AForm = window.location.pathname.includes('/503a/') && operationId && operationId !== 'new';

  const [openEqipGPTModal, setOpenEqipGPTModal] = useState(false);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [currentInspectionDetailUid, setCurrentInspectionDetailUid] = useState(null);
  const [isReadOnlyUser, setIsReadOnlyUser] = useState(false);
  const [userRoleName, setUserRoleName] = useState('');
  const [current503bOperation, setCurrent503bOperation] = useState(null);
  const [current503aOperation, setCurrent503aOperation] = useState(null);

  // Reset save guards when entering new forms
  useEffect(() => { if (isNew503BForm) saving503BRef.current = false; }, [isNew503BForm]);
  useEffect(() => { if (isNew503AForm) saving503ARef.current = false; }, [isNew503AForm]);

  // Check if current user has read-only role (role_id 99)
  const checkUserRole = async () => {
    try {
      const response = await ieqipCoreServiceApi.get(`api/assignmentassigntos/inspectionuid/${inspectionUid}`);
      const currentUserAssignment = response.data.find(assignment => 
        assignment.assign_to === loggedInUser.email
      );
      
      if (currentUserAssignment?.role_id === 99) {
        setIsReadOnlyUser(true);
        setUserRoleName('Read-Only Role');
        // Try to fetch role name
        // try {
        //   const roleResponse = await ieqipCoreServiceApi.get(`api/roles/${currentUserAssignment.role_id}`);
        //   setUserRoleName(roleResponse.data?.name || roleResponse.data?.role_name || 'Read-Only Role');
        // } catch (roleError) {
        //   console.error("Failed to fetch role name:", roleError);
        //   setUserRoleName('Read-Only Role'); // Fallback name
        // }
      } else {
        setIsReadOnlyUser(false);
        setUserRoleName('');
      }
    } catch (error) {
      console.error("Failed to check user role:", error);
      // Default to allowing edits if we can't determine role
      setIsReadOnlyUser(false);
      setUserRoleName('');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        hasUserInteracted.current = false; // ✅ Reset user-change flag
        
        // For new 503B form, only load inspection data and skip citation language lookup
        if (isNew503BForm) {
          const [responseInspection] = await Promise.all([
            inspectionApi.get(`/inspections/${inspectionUid}`),
            checkUserRole() // Check user role
          ]);
          const foundInspection = responseInspection.data.body[0];
          setInspection(foundInspection);
          
          // Set default empty values for new 503B operation
          setSelectedShortCitationLanguage(null);
          setSelectedCitationLanguage("503B New Operation");
          setCurrentInspectionDetailUid(null);
          setSelectedSystemChoice("1");
          setSelectedCoverageChoice("1");
          setSelectedRating("1");
          setInitialValues({
            coverageComment: "",
            supporting: "",
            specifically: "",
            discussion: "",
            capa: "",
            description: "",
          });
          
          setIsLoading(false);
          return;
        }

        // For new 503A form
        if (isNew503AForm) {
          const [responseInspection] = await Promise.all([
            inspectionApi.get(`/inspections/${inspectionUid}`),
            checkUserRole()
          ]);
          setInspection(responseInspection.data.body[0]);
          setSelectedShortCitationLanguage(null);
          setSelectedCitationLanguage("503A New Operation");
          setCurrentInspectionDetailUid(null);
          setSelectedSystemChoice("1");
          setSelectedCoverageChoice("1");
          setSelectedRating("1");
          setInitialValues({ coverageComment: "", supporting: "", specifically: "", discussion: "", capa: "", description: "" });
          setIsLoading(false);
          return;
        }

        // For existing 503A form, load the specific operation data
        if (isExisting503AForm) {
          const [responseInspection] = await Promise.all([
            inspectionApi.get(`/inspections/${inspectionUid}`),
            checkUserRole()
          ]);
          setInspection(responseInspection.data.body[0]);
          try {
            const response503A = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503aoperations`);
            const operations = response503A.data?.body || response503A.data || [];
            const operation503A = operations.find(
              op => op.inspection_503a_operation_uid === operationId ||
                    op.inspection_detail_uid === operationId ||
                    op.code === operationId
            );
            if (operation503A) {
              setCurrent503aOperation(operation503A);
              setSelectedShortCitationLanguage(null);
              setSelectedCitationLanguage(`503A Operation - ${operation503A.code}`);
              setCurrentInspectionDetailUid(operation503A.inspection_detail_uid);
              setSelectedSystemChoice(operation503A.detail?.systemChoice || "1");
              setSelectedCoverageChoice(operation503A.detail?.coverageChoice || "1");
              setSelectedRating(operation503A.detail?.rating || "1");
              setInitialValues({
                coverageComment: operation503A.detail?.coverageComment || "",
                supporting: operation503A.detail?.citationSupporting || "",
                specifically: operation503A.detail?.citationSpecifically || "",
                discussion: operation503A.detail?.citationDiscussion || "",
                capa: operation503A.detail?.citationCapa || "",
                description: operation503A.description || "",
              });
            } else {
              setSelectedCitationLanguage("503A Operation Not Found");
              setInitialValues({ coverageComment: "", supporting: "", specifically: "", discussion: "", capa: "", description: "" });
            }
          } catch (error) {
            console.error("Error loading 503A operation:", error);
            setSelectedCitationLanguage("Error Loading 503A Operation");
          }
          setIsLoading(false);
          return;
        }
        
        // For existing 503B form, load the specific operation data
        if (isExisting503BForm) {
          const [responseInspection] = await Promise.all([
            inspectionApi.get(`/inspections/${inspectionUid}`),
            checkUserRole() // Check user role
          ]);
          const foundInspection = responseInspection.data.body[0];
          setInspection(foundInspection);
          
          // Load 503B operations from the inspection data or API
          try {
            // Try both endpoint formats to get the 503B operations
            let response503B;
            try {
              response503B = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503boperations`);
            } catch (error) {
              console.log("Primary endpoint failed, trying alternative...");
              response503B = await ieqipCoreServiceApi.get(`/api/inspections/list/${inspectionUid}/inspection503boperations`);
            }
            
            console.log("503B API Response:", response503B.data);
            console.log("Looking for operationId:", operationId);
            console.log("Response structure:", {
              hasBody: !!response503B.data?.body,
              hasDirectData: !!response503B.data,
              bodyLength: response503B.data?.body?.length,
              directLength: Array.isArray(response503B.data) ? response503B.data.length : 'not array'
            });
            
            // Handle different response structures
            const operations = response503B.data?.body || response503B.data || [];
            console.log("Available operations:", operations.map(op => ({
              id: op.inspection_503b_operation_uid,
              detail_id: op.inspection_detail_uid,
              code: op.code,
              description: op.description
            })));
            
            const operation503B = operations.find(
              op => op.inspection_503b_operation_uid === operationId || 
                    op.inspection_detail_uid === operationId ||
                    op.code === operationId
            );
            
            console.log("Found operation503B:", operation503B);
            
            if (operation503B) {
              setCurrent503bOperation(operation503B);
              setSelectedShortCitationLanguage(null);
              setSelectedCitationLanguage(`503B Operation - ${operation503B.code}`);
              setCurrentInspectionDetailUid(operation503B.inspection_detail_uid);
              setSelectedSystemChoice(operation503B.detail?.systemChoice || "1");
              setSelectedCoverageChoice(operation503B.detail?.coverageChoice || "1");
              setSelectedRating(operation503B.detail?.rating || "1");
              const newInitialValues = {
                coverageComment: operation503B.detail?.coverageComment || "",
                supporting: operation503B.detail?.citationSupporting || "",
                specifically: operation503B.detail?.citationSpecifically || "",
                discussion: operation503B.detail?.citationDiscussion || "",
                capa: operation503B.detail?.citationCapa || "",
                description: operation503B.description || "",
              };
              console.log("Setting initial values for existing 503B:", newInitialValues);
              console.log("Operation data structure:", {
                detail: operation503B.detail,
                description: operation503B.description,
                code: operation503B.code
              });
              setInitialValues(newInitialValues);
            } else {
              // Operation not found, set empty values
              setSelectedCitationLanguage("503B Operation Not Found");
              setInitialValues({
                coverageComment: "",
                supporting: "",
                specifically: "",
                discussion: "",
                capa: "",
                description: "",
              });
            }
          } catch (error) {
            console.error("Error loading 503B operation:", error);
            setSelectedCitationLanguage("Error Loading 503B Operation");
          }
          
          setIsLoading(false);
          return;
        }
        
        const [responseInspection, responseShortCitationLanguage] = await Promise.all([
          inspectionApi.get(`/inspections/${inspectionUid}`),
          lookupcodetype ? 
            lookupApi.get(`/lookups/lookupcodes/${citationLanguageCode}/lookupcodetypes/${lookupcodetype}`) :
            lookupApi.get(`/lookups/lookupcodes/${citationLanguageCode}`),
          checkUserRole() // Check user role
        ]);
        const foundInspection = responseInspection.data.body[0];

        setInspection(foundInspection);

        cleanUp();

        // Set citation language
        const foundShortCitationLanguage = responseShortCitationLanguage.data.body[0];
        let foundLongCitationLanguage;
        if (foundShortCitationLanguage) {
          setSelectedShortCitationLanguage(foundShortCitationLanguage);
          foundLongCitationLanguage = await lookupApi.get(`/lookups/parent/${foundShortCitationLanguage?.lookup_uid}`);
          setSelectedCitationLanguage(foundLongCitationLanguage?.data?.body[0]?.description);
        }
        
        // fetch protocol quality
        if (
          foundInspection?.protocol_quality &&
          Array.isArray(foundInspection?.protocol_quality)
        ) {

          const foundProtocolQuality =
            foundInspection?.protocol_quality?.filter(
              (protocolQualityItem) =>
                protocolQualityItem.code === citationLanguageCode && 
                (lookupcodetype ? protocolQualityItem.lookupcodetype === lookupcodetype : true)
            )[0];

          if (!foundProtocolQuality?.inspection_detail_uid) {
            setCurrentInspectionDetailUid(null);
            return
          }

          setCurrentInspectionDetailUid(foundProtocolQuality.inspection_detail_uid);
          const responseInspectionDetail = await ieqipCoreServiceApi.get(`/api/inspections/inspectiondetails/${foundProtocolQuality?.inspection_detail_uid}`);
          setSelectedSystemChoice(responseInspectionDetail?.data.detail?.systemChoice);
          setSelectedCoverageChoice(
            responseInspectionDetail?.data?.detail?.coverageChoice
          );
          setSelectedRating(
            responseInspectionDetail?.data?.detail?.rating
          );
          setInitialValues({
            coverageComment: responseInspectionDetail?.data?.detail?.coverageComment,
            supporting: responseInspectionDetail?.data?.detail?.citationSupporting,
            specifically: responseInspectionDetail?.data?.detail?.citationSpecifically,
            discussion: responseInspectionDetail?.data?.detail?.citationDiscussion,
            capa: responseInspectionDetail?.data?.detail?.citationCapa,
          });

          // Set default citation language from database if any
          setSelectedCitationLanguage(responseInspectionDetail?.data?.detail?.citationLanguage ?? foundLongCitationLanguage?.data.body[0]?.description);
        }

        setIsLoading(false);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionUid, citationLanguageCode, lookupcodetype, isNew503BForm, isExisting503BForm, isNew503AForm, isExisting503AForm, operationId]);

  // Recreate debounced save whenever the captured values change (same pattern as CoverageDraggable)
  const doAutoSave = useCallback(() => {
    handleFormSubmit(initialValues);
  // eslint-disable-next-line
  }, [initialValues, selectedSystemChoice, selectedCoverageChoice, selectedRating]);

  useEffect(() => {
    if (debouncedSaveRef.current) debouncedSaveRef.current.cancel();
    debouncedSaveRef.current = _.debounce(() => { doAutoSave(); }, 2000);
    return () => { if (debouncedSaveRef.current) debouncedSaveRef.current.cancel(); };
  }, [doAutoSave]);

  // Trigger debounced save when any watched value changes
  useEffect(() => {
    if (!inspection || !hasUserInteracted.current || isReadOnlyUser) return;
    if (isNew503BForm || isNew503AForm) return;
    if ((isExisting503BForm || isExisting503AForm) && !initialValues.description?.trim()) return;
    if (debouncedSaveRef.current) debouncedSaveRef.current();
  // eslint-disable-next-line
  }, [
    initialValues.description,
    initialValues.coverageComment,
    initialValues.supporting,
    initialValues.specifically,
    initialValues.discussion,
    initialValues.capa,
    selectedSystemChoice,
    selectedCoverageChoice,
    selectedRating,
  ]);

  const handleSystemChoiceChange = (event) => {
    if (isReadOnlyUser) return;
    setSelectedSystemChoice(event.target.value);
  };

  const handleCoverageChange = (event) => {
    if (isReadOnlyUser) return;
    setSelectedCoverageChoice(event.target.value);
  };

  const handleRatingChange = (event) => {
    if (isReadOnlyUser) return;
    setSelectedRating(event.target.value);
  };

  const handleFormSubmit = async (values) => {
    if (isReadOnlyUser) {
      enqueueSnackbar("Cannot save - you are in read-only mode!", {
        variant: "warning",
        autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
      });
      return;
    }

    // Global lock: block any concurrent save
    if (saveInProgressRef.current) return;
    saveInProgressRef.current = true;

    try {
      setSaveStatus('saving');
      setIsLoading(true);
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
        debouncedSaveRef.current = null;
      }

      // Handle 503B operations separately
      if (isNew503BForm || isExisting503BForm) {
        if (saving503BRef.current) {
          setIsLoading(false);
          return;
        }
        const responseAssignmentSignatures = await assignmentApi.get(
          `/assignments/9/assignmentsignatures/inspectionuid/${inspectionUid}/assigntoemail/${loggedInUser.email}`
        );
        if (responseAssignmentSignatures?.data?.body?.id) {
          setErrorMessage('You cannot save because you have signed the report. Please delete the signature in order to making any changes')
          setIsLoading(false);
          return;
        }

        // Validate required fields for 503B operations
        if (!values.description || values.description.trim() === '') {
          enqueueSnackbar("Description is required for 503B operations. Please enter a description before saving.", {
            variant: "error",
            autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
          });
          setIsLoading(false);
          return;
        }

        // Generate a unique code for new operations only
        const operationCode = isNew503BForm ? `503B_${Date.now()}` : (current503bOperation?.code || `503B_${Date.now()}`);
        
        // For existing operations, always use the existing UID to prevent duplicates
        const operationUid = isNew503BForm ? uuidv4() : (current503bOperation?.inspection_503b_operation_uid || operationId);
        
        console.log("Operation determination:", {
          isNew503BForm,
          isExisting503BForm,
          operationId,
          current503bOperation: current503bOperation?.inspection_503b_operation_uid,
          finalOperationUid: operationUid,
          willCreate: isNew503BForm && !current503bOperation?.inspection_503b_operation_uid
        });
        
        const operation503BData = {
          inspection_503b_operation_uid: operationUid,
          inspection_id: inspection.id,
          code: operationCode,
          description: values.description || operationCode,
          lookup_code_type: "USFDA503B",
          detail: {
            rating: selectedRating,
            citationCapa: values.capa,
            systemChoice: selectedSystemChoice,
            coverageChoice: selectedCoverageChoice,
            coverageComment: values.coverageComment,
            citationDiscussion: values.discussion,
            citationSupporting: values.supporting,
            citationSpecifically: values.specifically
          },
          created_date: isNew503BForm ? new Date().toISOString() : current503bOperation?.created_date,
          created_by: isNew503BForm ? loggedInUser.email : current503bOperation?.created_by,
          updated_date: new Date().toISOString(),
          updated_by: loggedInUser.email,
          deleted_date: null
        };

        console.log("Submitting 503B operation data:", operation503BData);
        console.log("Operation type:", isNew503BForm ? "CREATE" : "UPDATE");

        try {
          // Determine if this is truly a new operation or an update
          const shouldCreateNew = isNew503BForm && !current503bOperation?.inspection_503b_operation_uid;
          
          if (shouldCreateNew) {
            // Create new 503B operation
            console.log("Creating new 503B operation...");
            const response = await create503bOperation(operation503BData);
            console.log("Create response:", response.data);
            if (response.data) {
              setCurrent503bOperation(response.data);
              setCurrentInspectionDetailUid(response.data.inspection_503b_operation_uid);
            enqueueSnackbar("503B Operation created successfully!", {
              variant: "success",
              autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
            });
            setSaveStatus('saved');
            // Dispatch event to update sidebar (remove dummy, add real operation)
            window.dispatchEvent(new CustomEvent('503bOperationCreated', {
              detail: { newOperation: response.data }
            }));
            
            // Navigate to the new operation's edit page to prevent future duplicates
            // This ensures subsequent saves will be updates, not creates
            window.location.href = `/inspection/${inspectionUid}/503b/${response.data.inspection_503b_operation_uid}`;
            }
          } else {
            // Update existing 503B operation
            console.log("Updating existing 503B operation...");
            const response = await update503bOperation(operation503BData);
            console.log("Update response:", response.data);
            
            // Dispatch event to update sidebar with updated operation
            window.dispatchEvent(new CustomEvent('503bOperationUpdated', {
              detail: { updatedOperation: operation503BData }
            }));
            
            enqueueSnackbar("503B Operation updated successfully!", {
              variant: "success",
              autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
            });
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
          }
        } catch (error) {
          console.error("503B Operation save error:", error);
          console.error("Error response:", error.response?.data);
          enqueueSnackbar(`Failed to save 503B Operation: ${error.response?.data?.detail || error.message}`, {
            variant: "error",
            autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
          });
          setIsLoading(false);
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 5000);
          return;
        }

        setIsLoading(false);
        saving503BRef.current = false;
        return;
      }

      // Handle 503A operations separately
      if (isNew503AForm || isExisting503AForm) {
        if (saving503ARef.current) {
          setIsLoading(false);
          return;
        }
        saving503ARef.current = true;

        const responseAssignmentSignatures = await assignmentApi.get(
          `/assignments/9/assignmentsignatures/inspectionuid/${inspectionUid}/assigntoemail/${loggedInUser.email}`
        );
        if (responseAssignmentSignatures?.data?.body?.id) {
          setErrorMessage('You cannot save because you have signed the report. Please delete the signature in order to making any changes');
          setIsLoading(false);
          saving503ARef.current = false;
          return;
        }

        if (!values.description || values.description.trim() === '') {
          enqueueSnackbar("Description is required for 503A operations. Please enter a description before saving.", {
            variant: "error",
            autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
          });
          setIsLoading(false);
          saving503ARef.current = false;
          return;
        }

        const operationCode = isNew503AForm ? `503A_${Date.now()}` : (current503aOperation?.code || `503A_${Date.now()}`);
        const operationUid = isNew503AForm ? uuidv4() : (current503aOperation?.inspection_503a_operation_uid || operationId);

        const operation503AData = {
          inspection_503a_operation_uid: operationUid,
          inspection_id: inspection.id,
          code: operationCode,
          description: values.description || operationCode,
          lookup_code_type: "USFDA503A",
          detail: {
            rating: selectedRating,
            citationCapa: values.capa,
            systemChoice: selectedSystemChoice,
            coverageChoice: selectedCoverageChoice,
            coverageComment: values.coverageComment,
            citationDiscussion: values.discussion,
            citationSupporting: values.supporting,
            citationSpecifically: values.specifically
          },
          created_date: isNew503AForm ? new Date().toISOString() : current503aOperation?.created_date,
          created_by: isNew503AForm ? loggedInUser.email : current503aOperation?.created_by,
          updated_date: new Date().toISOString(),
          updated_by: loggedInUser.email,
          deleted_date: null
        };

        try {
          const shouldCreateNew = isNew503AForm && !current503aOperation?.inspection_503a_operation_uid;

          if (shouldCreateNew) {
            if ((Date.now() - last503ASaveTimeRef.current) < SAVE_COOLDOWN_MS && last503ASaveTimeRef.current !== 0) {
              setIsLoading(false);
              saving503ARef.current = false;
              return;
            }
            const response = await create503aOperation(operation503AData);
            if (response.data) {
              setCurrent503aOperation(response.data);
              setCurrentInspectionDetailUid(response.data.inspection_503a_operation_uid);
              last503ASaveTimeRef.current = Date.now();
              enqueueSnackbar("503A Operation created successfully!", {
                variant: "success",
                autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
              });
              setSaveStatus('saved');
              window.dispatchEvent(new CustomEvent('503aOperationCreated', {
                detail: { newOperation: response.data }
              }));
              navigate(`/inspection/${inspectionUid}/503a/${response.data.inspection_503a_operation_uid}`);
            }
          } else {
            if ((Date.now() - last503ASaveTimeRef.current) < SAVE_COOLDOWN_MS) {
              setIsLoading(false);
              saving503ARef.current = false;
              return;
            }
            await update503aOperation(operation503AData);
            last503ASaveTimeRef.current = Date.now();
            window.dispatchEvent(new CustomEvent('503aOperationUpdated', {
              detail: { updatedOperation: operation503AData }
            }));
            enqueueSnackbar("503A Operation updated successfully!", {
              variant: "success",
              autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
            });
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
          }
        } catch (error) {
          console.error("503A Operation save error:", error);
          enqueueSnackbar(`Failed to save 503A Operation: ${error.response?.data?.detail || error.message}`, {
            variant: "error",
            autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
          });
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 5000);
        }

        setIsLoading(false);
        saving503ARef.current = false;
        return;
      }

      const responseAssignmentSignatures = await assignmentApi.get(
        `/assignments/9/assignmentsignatures/inspectionuid/${inspectionUid}/assigntoemail/${loggedInUser.email}`
      );
      if (responseAssignmentSignatures?.data?.body?.id) {
        setErrorMessage('You cannot save because you have signed the report. Please delete the signature in order to making any changes')
        setIsLoading(false);
        return;
      }

      const newOrUpdateInspectionDetail = {
        inspection_uid: inspectionUid,
        inspection_id: inspection.id,
        code: citationLanguageCode,
        lookup_code_type: lookupcodetype ?? 'GENERAL',
        detail: {
          systemChoice: selectedSystemChoice,
          coverageChoice: selectedCoverageChoice,
          coverageComment: values.coverageComment,
          citationDiscussion: values.discussion,
          citationSupporting: values.supporting,
          citationSpecifically: values.specifically,
          citationLanguage: selectedCitationLanguage,
          citationCapa: values.capa,
          rating: selectedRating,
        },
        created_date: getUTCDateTime(),
        created_by: loggedInUser.email,
        updated_date: null,
        updated_by: null,
        deleted_date: null
      }
      const responseInspectionDetail = await ieqipCoreServiceApi.post("/api/inspections/inspectiondetails", newOrUpdateInspectionDetail);

      // Update the current inspection detail UID for future deletes
      setCurrentInspectionDetailUid(responseInspectionDetail?.data?.inspection_detail_uid);

      const newOrUpdatedProtocolQuality = [
        {
          code: citationLanguageCode,
          lookupcodetype: lookupcodetype,
          inspection_detail_uid: responseInspectionDetail?.data?.inspection_detail_uid
        },
      ];

      // Ensure inspection.protocol_quality is initialized as an array
      if (!Array.isArray(inspection.protocol_quality)) {
        inspection.protocol_quality = [];
      }

      // Ask Chat GPT 
      // Message 1: merge 2 array object with 2 primary elements in javascript
      // Message 2: no, it isn't result I'm looking for. I want to merge only the different element in each array
      const mergedArray = [...inspection.protocol_quality.filter(item => item.code !== newOrUpdatedProtocolQuality[0].code), ...newOrUpdatedProtocolQuality];
      inspection.protocol_quality = mergedArray;

      await inspectionApi.put(`/inspections/${inspectionUid}`, inspection);

      setIsLoading(false);
      setSaveStatus('saved');
      // Auto-hide "saved" status after 3 seconds
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save", error);
      setErrorMessage(`Failed to save the inspection. Error: ${error}`)
      setIsLoading(false);
      setSaveStatus('error');
      // Auto-hide error status after 5 seconds
      setTimeout(() => setSaveStatus('idle'), 5000);
    } finally {
      saveInProgressRef.current = false;
    }
  };

  const handleDelete = async () => {
    if (isReadOnlyUser) {
      enqueueSnackbar("Cannot delete - you are in read-only mode!", {
        variant: "warning",
        autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
      });
      return;
    }

    // 503B delete
    if (isExisting503BForm) {
      const confirmDelete = window.confirm("Are you sure you want to delete this 503B Operation? This action cannot be undone.");
      if (!confirmDelete) return;

      try {
        setIsLoading(true);
        const responseAssignmentSignatures = await assignmentApi.get(
          `/assignments/9/assignmentsignatures/inspectionuid/${inspectionUid}/assigntoemail/${loggedInUser.email}`
        );
        if (responseAssignmentSignatures?.data?.body?.id) {
          setErrorMessage('You cannot delete because you have signed the report. Please delete the signature in order to make any changes');
          setIsLoading(false);
          return;
        }

        const operationUid = current503bOperation?.inspection_503b_operation_uid || operationId;
        await delete503bOperation(operationUid);

        window.dispatchEvent(new CustomEvent('503bOperationDeleted', {
          detail: { deletedOperationUid: operationUid }
        }));

        enqueueSnackbar("503B Operation deleted successfully!", {
          variant: "success",
          autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
        });
        setIsLoading(false);
        navigate(`/inspection/${inspectionUid}/503b/new`);
      } catch (error) {
        console.error("Failed to delete 503B operation", error);
        setErrorMessage(`Failed to delete the 503B Operation. Error: ${error.response?.data?.detail || error.message}`);
        setIsLoading(false);
      }
      return;
    }

    // 503A delete
    if (isExisting503AForm) {
      const confirmDelete = window.confirm("Are you sure you want to delete this 503A Operation? This action cannot be undone.");
      if (!confirmDelete) return;

      try {
        setIsLoading(true);
        const responseAssignmentSignatures = await assignmentApi.get(
          `/assignments/9/assignmentsignatures/inspectionuid/${inspectionUid}/assigntoemail/${loggedInUser.email}`
        );
        if (responseAssignmentSignatures?.data?.body?.id) {
          setErrorMessage('You cannot delete because you have signed the report. Please delete the signature in order to make any changes');
          setIsLoading(false);
          return;
        }

        const operationUid = current503aOperation?.inspection_503a_operation_uid || operationId;
        await delete503aOperation(operationUid);

        window.dispatchEvent(new CustomEvent('503aOperationDeleted', {
          detail: { deletedOperationUid: operationUid }
        }));

        enqueueSnackbar("503A Operation deleted successfully!", {
          variant: "success",
          autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
        });
        setIsLoading(false);
        navigate(`/inspection/${inspectionUid}/503a/new`);
      } catch (error) {
        console.error("Failed to delete 503A operation", error);
        setErrorMessage(`Failed to delete the 503A Operation. Error: ${error.response?.data?.detail || error.message}`);
        setIsLoading(false);
      }
      return;
    }

    // Standard inspection detail delete
    if (!currentInspectionDetailUid) {
      enqueueSnackbar("No inspection detail to delete!", {
        variant: "warning",
        autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
      });
      return;
    }

    const confirmDelete = window.confirm("Are you sure you want to delete this inspection detail? This action cannot be undone.");
    if (!confirmDelete) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Check if user has signed the report
      const responseAssignmentSignatures = await assignmentApi.get(
        `/assignments/9/assignmentsignatures/inspectionuid/${inspectionUid}/assigntoemail/${loggedInUser.email}`
      );
      if (responseAssignmentSignatures?.data?.body?.id) {
        setErrorMessage('You cannot delete because you have signed the report. Please delete the signature in order to make any changes');
        setIsLoading(false);
        return;
      }

      // Delete the inspection detail
      await ieqipCoreServiceApi.delete(`/api/inspections/inspectiondetails/${currentInspectionDetailUid}`);

      // Remove the protocol quality from the inspection
      if (inspection?.protocol_quality && Array.isArray(inspection.protocol_quality)) {
        inspection.protocol_quality = inspection.protocol_quality.filter(item => 
          !(item.code === citationLanguageCode && item.lookupcodetype === lookupcodetype)
        );
        await inspectionApi.put(`/inspections/${inspectionUid}`, inspection);
      }

      // Reset form state
      cleanUp();
      setCurrentInspectionDetailUid(null);

      enqueueSnackbar("Inspection detail deleted successfully!", {
        variant: "success",
        autoHideDuration: SNACKBAT_AUTO_HIDE_DURATION,
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Failed to delete", error);
      setErrorMessage(`Failed to delete the inspection detail. Error: ${error}`);
      setIsLoading(false);
    }
  };

  // reset data
  const cleanUp = () => {
    setSelectedSystemChoice(null);
    setSelectedCoverageChoice(null);
    setSelectedRating(null);
    setInitialValues(prevValues => ({
      ...prevValues,
      coverageComment: null,
      supporting: null,
      specifically: null,
      discussion: null,
      capa: null,
      description: null,
    }));
  };

  // Begin EqipGPT modal
  const onHandleEqipGPTModalCancel = () => {
    setOpenEqipGPTModal(false);
  }

  // Begin Upload File modal
  const onHandleFileModalOk = () => {
    setOpenFileModal(false);
  }

  const onHandleFileModalCancel = () => {
    setOpenFileModal(false);
  }

  const onHandleFileIconClick = (event) => {
    setOpenFileModal(true);
    event.stopPropagation()
  }
  // End Upload File modal

  // Begin Note modal
  const onHandleNoteModalCancel = () => {
    setOpenNoteModal(false);
  }

  return (
    <Box m="20px">
      {/* Save Status Indicator */}
      <Fade in={saveStatus !== 'idle'}>
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            right: 20,
            zIndex: 1200,
          }}
        >
          {saveStatus === 'saving' && (
            <Chip
              icon={<CloudSyncIcon />}
              label="Saving..."
              color="info"
              size="small"
              sx={{
                fontWeight: 500,
                boxShadow: 2,
                '& .MuiChip-icon': {
                  animation: 'spin 1s linear infinite',
                },
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          )}
          {saveStatus === 'saved' && (
            <Chip
              icon={<CloudDoneIcon />}
              label="All changes saved"
              color="success"
              size="small"
              sx={{
                fontWeight: 500,
                boxShadow: 2,
              }}
            />
          )}
          {saveStatus === 'error' && (
            <Chip
              icon={<ErrorOutlineIcon />}
              label="Failed to save"
              color="error"
              size="small"
              sx={{
                fontWeight: 500,
                boxShadow: 2,
              }}
            />
          )}
        </Box>
      </Fade>
      
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading && saveStatus !== 'saving'}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {errorMessage.length > 0 && 
      <Alert onClose={() => setErrorMessage('')} 
                severity="error"
                sx={{ marginBottom: '0.5em' }}
              >
                {errorMessage}
      </Alert>
      }
      {isReadOnlyUser && 
      <Alert 
                severity="info"
                sx={{ marginBottom: '0.5em' }}
              >
                {`You are in read-only mode. You cannot edit this inspection because you have "${userRoleName}".`}
      </Alert>
      }
      <Formik
        // onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={getValidationSchema(isNew503BForm, isExisting503BForm, isNew503AForm, isExisting503AForm)}
        enableReinitialize={true} // Important: allow to reload data on useEffect
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="10px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <FormControl sx={{ gridColumn: "span 4" }}>
                <SimpleAccordin title={selectedShortCitationLanguage?.description}>
                  {getCitationLanguageWithoutPlaceHolder(selectedCitationLanguage)}
                </SimpleAccordin>
                {!isReadOnlyUser && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row-reverse',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CloudUpload />}
                      onClick={onHandleFileIconClick}
                    >
                      Exhibit
                    </Button>
                  </Box>
                )}  
              </FormControl>
              {!existRegulationWithNoCitation(citationLanguageCode) && ( // don't show system choice and coverage choice if there is notication
                <>
                  <FormControl sx={{ gridColumn: "span 4" }}>
                    <FormLabel id="demo-radio-buttons-group-label">
                      System Choice
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="system-choice-radio-buttons-group-label"
                      name="system-choice-radio-buttons-group"
                      row
                      value={selectedSystemChoice ?? null}
                      onChange={handleSystemChoiceChange}
                      disabled={isReadOnlyUser}
                    >
                      {isFoundSystemChoicesBySubPartCode(subPartCode, 'Quality') && <FormControlLabel
                        value="35fad871-5a30-4937-b4c3-e66b070f9cf1"
                        control={<Radio />}
                        label="Quality"
                      />}
                      {isFoundSystemChoicesBySubPartCode(subPartCode, 'Facilities/Equipment') && <FormControlLabel
                        value="e6f02680-948f-4b1b-9138-b4bc608f126d"
                        control={<Radio />}
                        label="Facilities/Equipment"
                      />}
                      {isFoundSystemChoicesBySubPartCode(subPartCode, 'Materials') && <FormControlLabel
                        value="a5dd5bef-4def-4d7d-9113-0dee6c9876f4"
                        control={<Radio />}
                        label="Materials"
                      />}
                      {isFoundSystemChoicesBySubPartCode(subPartCode, 'Production') && <FormControlLabel
                        value="bcb11489-0473-4199-90db-1d1ea7d36250"
                        control={<Radio />}
                        label="Production"
                      />}
                      {isFoundSystemChoicesBySubPartCode(subPartCode, 'Packaging/Labeling') && <FormControlLabel
                        value="d18f6840-0d95-4188-894f-a5f784b3fa52"
                        control={<Radio />}
                        label="Packaging/Labeling"
                      />}
                      {isFoundSystemChoicesBySubPartCode(subPartCode, 'Laboratory') && <FormControlLabel
                        value="92d55e3d-6901-4396-8a87-ba9d6b3ea0d4"
                        control={<Radio />}
                        label="Laboratory"
                      />}
                      {/* Add "Other" option for 503B/503A operations */}
                      {(isNew503BForm || isExisting503BForm || isNew503AForm || isExisting503AForm) && <FormControlLabel
                        value="5df57e3c-304b-4386-b52f-02fcd66d53e5"
                        control={<Radio />}
                        label="Other"
                      />}
                    </RadioGroup>
                  </FormControl>
                  <FormControl sx={{ gridColumn: "span 4" }}>
                    <FormLabel id="coverage-radio-buttons-group-label">
                      Coverage
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="coverage-radio-buttons-group-label"
                      name="coverage-radio-buttons-group"
                      row
                      value={selectedCoverageChoice ?? null}
                      onChange={handleCoverageChange}
                      disabled={isReadOnlyUser}
                    >
                      <FormControlLabel
                        value="0d0511a1-d98f-45ec-aef4-9f6c94cbd102"
                        control={<Radio />}
                        label="Observation/Major Finding"
                      />
                      <FormControlLabel
                        value="a499a5e7-0d55-4d91-867d-cbe852f9e2e4"
                        control={<Radio />}
                        label="Discussion/Minor Finding"
                      />
                      <FormControlLabel
                        value="ba553ae7-3c14-4d10-a317-1ae563c17e8e"
                        control={<Radio />}
                        label="Covered/No Finding"
                      />
                      <FormControlLabel
                        value="27375d7f-5e28-44be-8833-0bc82b872ebd"
                        control={<Radio />}
                        label="Not Covered"
                      />
                    </RadioGroup>
                  </FormControl>
                  <FormControl sx={{ gridColumn: "span 4" }}>
                    <FormLabel id="rating-radio-buttons-group-label">
                      Rating
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="rating-radio-buttons-group-label"
                      name="rating-radio-buttons-group"
                      row
                      value={selectedRating ?? null}
                      onChange={handleRatingChange}
                      disabled={isReadOnlyUser}
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio sx={{ color: red[900], '&.Mui-checked': { color: red[900], }}}/>}
                        label="1"
                        sx={{ color: red[900]}}
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio sx={{ color: red[800], '&.Mui-checked': { color: red[800], }}}/>}
                        label="2"
                        sx={{ color: red[800]}}
                      />
                      <FormControlLabel
                        value="3"
                        control={<Radio sx={{ color: red[700], '&.Mui-checked': { color: red[700], }}}/>}
                        label="3"
                        sx={{ color: red[700]}}
                      />
                      <FormControlLabel
                        value="4"
                        control={<Radio sx={{ color: yellow[700], '&.Mui-checked': { color: yellow[700], }}}/>}
                        label="4"
                        sx={{ color: yellow[700]}}
                      />
                      <FormControlLabel
                        value="5"
                        control={<Radio sx={{ color: yellow[600], '&.Mui-checked': { color: yellow[600], }}}/>}
                        label="5"
                        sx={{ color: yellow[600]}}
                      />
                      <FormControlLabel
                        value="6"
                        control={<Radio sx={{ color: yellow[500], '&.Mui-checked': { color: yellow[500], }}}/>}
                        label="6"
                        sx={{ color: yellow[500]}}
                      />
                      <FormControlLabel
                        value="7"
                        control={<Radio sx={{ color: green[900], '&.Mui-checked': { color: green[900], }}}/>}
                        label="7"
                        sx={{ color: green[900]}}
                      />
                      <FormControlLabel
                        value="8"
                        control={<Radio sx={{ color: green[800], '&.Mui-checked': { color: green[800], }}}/>}
                        label="8"
                        sx={{ color: green[800]}}
                      />
                      <FormControlLabel
                        value="9"
                        control={<Radio sx={{ color: green[700], '&.Mui-checked': { color: green[700], }}}/>}
                        label="9"
                        sx={{ color: green[700]}}
                      />
                      <FormControlLabel
                        value="10"
                        control={<Radio sx={{ color: blue[600], '&.Mui-checked': { color: blue[600], }}}/>}
                        label="10"
                        sx={{ color: blue[600]}}
                      />
                    </RadioGroup>
                  </FormControl>
                </>
              )}
              {/* <TextField
                fullWidth
                variant="filled"
                multiline
                type="text"
                label="Comment for report body"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.coverageComment ?? ""}
                name="coverageComment"
                // error={!!touched.lastName && !!errors.lastName}
                // helperText={touched.lastName && errors.lastName}
                sx={{ gridColumn: "span 4" }}
              /> */}
              {/* Description field for 503B/503A operations */}
              {(isNew503BForm || isExisting503BForm || isNew503AForm || isExisting503AForm) && (
                <FormControl sx={{ gridColumn: "span 4", marginBottom: "16px" }}>
                  <FormLabel sx={{ marginBottom: "8px", fontWeight: "bold" }}>
                    Description
                  </FormLabel>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="text"
                    placeholder={`Enter description for this ${isNew503AForm || isExisting503AForm ? '503A' : '503B'} operation`}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      if (isReadOnlyUser || saveStatus === 'saving' || isExisting503BForm || isExisting503AForm) return;
                      hasUserInteracted.current = true;
                      setFieldValue('description', e.target.value);
                      setInitialValues((prev) => ({ ...prev, description: e.target.value }));
                    }}
                    value={values.description || ""}
                    name="description"
                    readOnly={isReadOnlyUser || isExisting503BForm || isExisting503AForm}
                    disabled={saveStatus === 'saving' || isExisting503BForm || isExisting503AForm}
                    error={!!touched.description && !!errors.description}
                    helperText={touched.description && errors.description}
                    sx={{ backgroundColor: (isReadOnlyUser || isExisting503BForm || isExisting503AForm) ? '#f5f5f5' : 'white' }}
                    required
                  />
                </FormControl>
              )}
              
              <FormControl sx={{ gridColumn: "span 4" }}>
                <EqipRichTextEditor fullWidth theme="snow"
                  value={values.coverageComment} 
                    onChange={(content) => {
                      if (isReadOnlyUser) return;
                      hasUserInteracted.current = true;
                      setFieldValue('coverageComment', content);
                      setInitialValues((prev) => ({ ...prev, coverageComment: content }));
                    }}
                  onBlur={handleBlur}
                  placeholder="Comment for report body"
                  readOnly={isReadOnlyUser}
                  isA4Format={true}/>
              </FormControl>
              {(selectedCoverageChoice === COVERAGE_CHOICE_CITATION_GUID || selectedCoverageChoice === COVERAGE_CHOICE_COVERED_DISCUSS_ONLY_GUID) && (
                <>
                  {!isReadOnlyUser && (
                    <FormControl sx={{ gridColumn: "span 4", marginLeft: "10px" }}>
                      <CitationStatementChip 
                        selectedCitationLanguage={selectedCitationLanguage}
                        setSelectedCitationLanguage={setSelectedCitationLanguage}  />
                    </FormControl>
                  )}
                  <FormControl sx={{ gridColumn: "span 4", marginLeft: "10px" }}>
                    <FormLabel sx={{ marginBottom: "8px", fontWeight: "bold" }}>
                      Specifically
                    </FormLabel>
                    <EqipRichTextEditor fullWidth theme="snow" 
                      value={values.specifically}
                      onChange={(content) => {
                        if (isReadOnlyUser) return;
                        hasUserInteracted.current = true;
                        setFieldValue('specifically', content);
                        setInitialValues((prev) => ({ ...prev, specifically: content }));
                      }}
                      onBlur={handleBlur}
                      placeholder="Specifically"
                      readOnly={isReadOnlyUser}
                      isA4Format={true}/>
                  </FormControl>
                  <FormControl sx={{ gridColumn: "span 4", marginLeft: "10px" }}>
                    <FormLabel sx={{ marginBottom: "8px", fontWeight: "bold" }}>
                      Supporting Evidence and Relevance
                    </FormLabel>
                    <EqipRichTextEditor fullWidth theme="snow" 
                      value={values.supporting}
                      onChange={(content) => {
                        if (isReadOnlyUser) return;
                        hasUserInteracted.current = true;
                        setFieldValue('supporting', content);
                        setInitialValues((prev) => ({ ...prev, supporting: content }));
                      }}
                      onBlur={handleBlur}
                      placeholder="Supporting evidence and relevance"
                      readOnly={isReadOnlyUser}
                      isA4Format={true}/>
                  </FormControl>
                  <FormControl sx={{ gridColumn: "span 4", marginLeft: "10px" }}>
                    <FormLabel sx={{ marginBottom: "8px", fontWeight: "bold" }}>
                      Recommendation
                    </FormLabel>
                    <EqipRichTextEditor fullWidth theme="snow" 
                      value={values.discussion}
                      onChange={(content) => {
                        if (isReadOnlyUser) return;
                        hasUserInteracted.current = true;
                        setFieldValue('discussion', content);
                        setInitialValues((prev) => ({ ...prev, discussion: content }));
                      }}
                      onBlur={handleBlur}
                      placeholder="Recommendation"
                      readOnly={isReadOnlyUser}
                      isA4Format={true}/>
                  </FormControl>
                  <FormControl sx={{ gridColumn: "span 4", marginLeft: "10px" }}>
                    <FormLabel sx={{ marginBottom: "8px", fontWeight: "bold" }}>
                      Corrective Action Preventive Action (CAPA)
                    </FormLabel>
                    <EqipRichTextEditor fullWidth theme="snow" 
                      value={values.capa}
                      onChange={(content) => {
                        if (isReadOnlyUser) return;
                        hasUserInteracted.current = true;
                        setFieldValue('capa', content);
                        setInitialValues((prev) => ({ ...prev, capa: content }));
                      }}
                      onBlur={handleBlur}
                      placeholder="Corrective Action Preventive Action (CAPA)"
                      readOnly={isReadOnlyUser}
                      isA4Format={true}/>
                  </FormControl>
                </>
              )}
            </Box>
            {!isReadOnlyUser && (
              <Box
                display="flex"
                justifyContent="end"
                gap="10px"
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 10,
                  backgroundColor: 'background.default',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                  px: 2,
                  mt: 2,
                }}
              >
                {(currentInspectionDetailUid || isExisting503BForm || isExisting503AForm) && (
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={handleDelete}
                    startIcon={<DeleteIcon />}
                    disabled={saveStatus === 'saving'}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  onClick={() => handleFormSubmit(values)} //use this if formik submit doesn't trigger
                  startIcon={<SaveIcon />}
                  disabled={saveStatus === 'saving'}
                >
                  {saveStatus === 'saving' ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            )}
            <Stack spacing={2} direction="row">
              <ModalDialog
                title="Files"
                open={openFileModal}
                hideCancel={true}
                content={<Attachment uploadFileParams={{ 
                  inspectionUid, 
                  code: citationLanguageCode }} />}
                handleOk={onHandleFileModalOk}
                handleCancel={onHandleFileModalCancel}
              />
              <ModalDialog
                title="Reference"
                open={openNoteModal}
                hideCancel={true}
                hideOK={true}
                content={<NoteForm inspection_uid={inspectionUid} code={citationLanguageCode} onClose={onHandleNoteModalCancel} />}
              />
              <ModalDialog
                title="Ask ieQip"
                open={openEqipGPTModal}
                hideCancel={true}
                content={<GptForm />}
                handleOk={onHandleEqipGPTModalCancel}
              />
            </Stack>
          </form>
        )}
      </Formik>
    </Box>
  );
};

// Dynamic validation schema based on form type
const getValidationSchema = (isNew503BForm, isExisting503BForm, isNew503AForm, isExisting503AForm) => {
  return yup.object().shape({
    comment: yup.string(),
    supporting: yup.string(),
    specifically: yup.string(),
    discussion: yup.string(),
    capa: yup.string(),
    description: (isNew503BForm || isExisting503BForm || isNew503AForm || isExisting503AForm)
      ? yup.string().required("Description is required for 503B/503A operations")
      : yup.string(),
  });
};

export default Form;
